import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useHint } from "@/hooks/useHint";
import { filterSummary, isEmptyFilter, savedFilter, useGenres } from "@/components/FilterEditor";
import { useProviders } from "@/components/WatchFilter";
import { Bookmark, SlidersHorizontal, HelpCircle, Loader2, RotateCcw, Sparkles, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, posterUrl } from "@/lib/api";
import { SwipeCard, CardBehind } from "@/components/SwipeCard";
import { SwipeActions } from "@/components/SwipeActions";
import { SwipeTutorial } from "@/components/SwipeTutorial";
import { MovieDetails } from "@/components/MovieDetails";
import { TmdbAttribution } from "@/components/Brand";

export const SWIPE_TOASTS = {
  right: { label: "Added to Watchlist", icon: Bookmark, color: "hsl(var(--sp-right))" },
  left: { label: "Not interested · won't show again", icon: X, color: "hsl(var(--sp-left))" },
  up: { label: "Seen · Liked it", icon: ThumbsUp, color: "hsl(var(--sp-up))" },
  down: { label: "Seen · Didn't like it", icon: ThumbsDown, color: "hsl(var(--sp-down))" },
};
const KEYS = { ArrowRight: "right", ArrowLeft: "left", ArrowUp: "up", ArrowDown: "down" };

function FilterChip() {
  const { user } = useAuth();
  const genres = useGenres();
  useProviders();
  const profile = user.active_profile || "default";
  const flt = savedFilter(user, profile);
  if (profile === "default" && isEmptyFilter(flt)) return null;
  return (
    <Link to="/genres" data-testid="discover-filter-chip" data-filter-profile={profile}
      className={`max-w-full flex items-center gap-2 h-8 px-3 rounded-full border text-xs font-semibold transition-colors hover:bg-accent ${profile === "mood" ? "border-primary/50 text-primary" : "text-muted-foreground"}`}>
      <SlidersHorizontal className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{profile === "mood" ? "Current mood" : "Default"}: {filterSummary(flt, genres)}</span>
    </Link>
  );
}

function TasteMeter({ stats, onHelp }) {
  if (!stats) return null;
  const pct = Math.min(100, (stats.swipes / stats.personalize_after) * 100);
  return (
    <div className="flex items-center gap-3 text-xs font-semibold text-muted-foreground" data-testid="taste-meter">
      {stats.personalized ? (
        <span className="flex items-center gap-1.5 text-primary"><Sparkles className="h-4 w-4" /> Tuned to your taste</span>
      ) : (
        <>
          <span>Learning your taste</span>
          <span className="w-24 h-1.5 rounded-full bg-secondary overflow-hidden"><span className="block h-full bg-primary transition-[width] duration-500" style={{ width: `${pct}%` }} /></span>
          <span className="font-mono-sp">{stats.swipes}/{stats.personalize_after}</span>
        </>
      )}
      <button onClick={onHelp} aria-label="How to swipe" data-testid="swipe-help-button" className="hover:text-foreground transition-colors">
        <HelpCircle className="h-4 w-4" />
      </button>
    </div>
  );
}

function useFeed() {
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exhausted, setExhausted] = useState(false);
  const queueRef = useRef([]);
  const fetching = useRef(false);
  queueRef.current = queue;

  const load = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const exclude = queueRef.current.map((m) => m.movie_id).join(",");
      const { data } = await api.get("/feed", { params: { exclude, count: 20 } });
      setQueue((q) => {
        const ids = new Set(q.map((m) => m.movie_id));
        return [...q, ...data.movies.filter((m) => !ids.has(m.movie_id))];
      });
      setStats(data.meta);
      setExhausted(data.movies.length === 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      fetching.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!loading && queue.length < 6) load(); }, [queue.length, loading, load]);
  return { queue, setQueue, queueRef, stats, setStats, loading, exhausted };
}

export default function Discover() {
  const { queue, setQueue, queueRef, stats, setStats, loading, exhausted } = useFeed();
  const [history, setHistory] = useState([]);
  const [undoing, setUndoing] = useState(false);
  const [details, setDetails] = useState(null);
  const hint = useHint("discover-swipe");
  const [reopened, setReopened] = useState(false);
  const tutorial = reopened || !hint.dismissed;
  const pending = useRef(Promise.resolve());
  const handles = useRef({});
  const [exitDir, setExitDir] = useState("left");
  // Always act on the movie currently on top, never a card that is still animating out.
  const flyTop = useCallback((dir) => {
    const current = queueRef.current[0];
    if (current) handles.current[current.movie_id]?.fly(dir);
  }, [queueRef]);

  const { dismiss: dismissHint } = hint;
  const dismissTutorial = useCallback(() => { setReopened(false); dismissHint(); }, [dismissHint]);

  const handleSwipe = useCallback((movie, direction) => {
    dismissTutorial();
    setExitDir(direction);
    delete handles.current[movie.movie_id];
    queueRef.current = queueRef.current.filter((m) => m.movie_id !== movie.movie_id);
    setQueue((q) => q.filter((m) => m.movie_id !== movie.movie_id));
    setHistory((h) => [...h.slice(-19), movie]);
    const { label, icon: Icon, color } = SWIPE_TOASTS[direction];
    toast(label, {
      id: "swipe-feedback",
      description: movie.title,
      duration: 2200,
      icon: <Icon className="h-4 w-4" style={{ color }} />,
      action: { label: "Undo", onClick: () => undoRef.current() },
    });
    pending.current = api.post("/swipe", { movie_id: movie.movie_id, direction, source: "discovery" })
      .then(({ data }) => setStats(data.stats))
      .catch((e) => {
        if (e.response?.status === 409) toast("Already rated", { id: "swipe-feedback", description: movie.title });
        else toast.error(`Couldn't save "${movie.title}": ${formatApiError(e)}`, { id: "swipe-feedback" });
      });
  }, [setQueue, setStats, queueRef, dismissTutorial]);

  const undo = useCallback(async () => {
    const movie = history[history.length - 1];
    if (!movie || undoing) return;
    setUndoing(true);
    try {
      await pending.current;
      const { data } = await api.post("/swipe/undo", { movie_id: movie.movie_id });
      setHistory((h) => h.slice(0, -1));
      setQueue((q) => [data.movie, ...q.filter((m) => m.movie_id !== movie.movie_id)]);
      setStats(data.stats);
      toast("Swipe undone", { id: "swipe-feedback", description: `${movie.title} is back on top`, duration: 1600 });
    } catch (e) {
      setHistory([]);
      toast.error(formatApiError(e));
    } finally {
      setUndoing(false);
    }
  }, [history, undoing, setQueue, setStats]);
  const undoRef = useRef(undo);
  undoRef.current = undo;

  useEffect(() => {
    const onKey = (e) => {
      if (details || e.repeat) return;
      if ((e.key === "z" || e.key === "Backspace") && !e.target.closest?.("input,textarea")) { undo(); return; }
      const dir = KEYS[e.key];
      if (dir) { e.preventDefault(); flyTop(dir); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [details, undo, flyTop]);

  const [top, next] = queue;
  const upcoming = queue.slice(1, 5).map((m) => m.poster_path).join(",");
  useEffect(() => {
    // Warm the cache so each card's poster is ready the moment it reaches the top.
    upcoming.split(",").filter(Boolean).forEach((p) => { new Image().src = posterUrl(p, "w780"); });
  }, [upcoming]);
  return (
    <div className="max-w-md mx-auto flex flex-col items-center gap-5" data-testid="discover-page">
      <TasteMeter stats={stats} onHelp={() => setReopened(true)} />
      <FilterChip />
      <div className="relative w-full h-[560px] max-h-[64vh]">
        {top ? (
          <>
            <CardBehind movie={next} />
            <AnimatePresence custom={exitDir}>
              <SwipeCard key={top.movie_id} ref={(h) => { if (h) handles.current[top.movie_id] = h; }} movie={top} exitInParent
                onSwipe={(d) => handleSwipe(top, d)} onDetails={setDetails} />
            </AnimatePresence>
            <AnimatePresence>{tutorial && <SwipeTutorial onDismiss={dismissTutorial} />}</AnimatePresence>
          </>
        ) : exhausted ? (
          <div className="absolute inset-0 rounded-3xl border border-dashed flex flex-col items-center justify-center gap-3 text-center p-8 text-muted-foreground" data-testid="discover-filter-exhausted">
            <SlidersHorizontal className="h-8 w-8 text-primary" />
            <p className="text-sm">No more movies match your current filter.</p>
            <Link to="/genres" className="text-primary font-semibold text-sm" data-testid="discover-filter-exhausted-link">Adjust filter</Link>
          </div>
        ) : (
          <div className="absolute inset-0 rounded-3xl border border-dashed flex flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="discover-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm">{loading ? "Finding movies for you…" : "Loading more picks…"}</p>
          </div>
        )}
      </div>
      <SwipeActions disabled={!top} onAction={flyTop} />
      <button onClick={undo} disabled={!history.length || undoing} data-testid="swipe-undo-button"
        className="flex items-center gap-2 h-9 px-4 rounded-full border text-xs font-semibold text-muted-foreground transition-[opacity,background-color] hover:bg-accent disabled:opacity-30">
        {undoing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Undo last swipe
      </button>
      <p className="text-xs text-muted-foreground hidden sm:block">Tip: drag the card, use arrow keys, or press Z to undo</p>
      <TmdbAttribution compact />
      <MovieDetails movie={details} onClose={() => setDetails(null)} />
    </div>
  );
}
